import React from 'react';
import { formatTime } from '../../utils/date';
import { ChildVM } from './useLiveSession';

function initialsOf(name: string): string {
  return (
    name
      .split(' ')
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  );
}

/** Soft avatar tint per status — a calm signal, not a loud fill. */
const AVATAR_TINT: Record<ChildVM['status'], string> = {
  here: 'bg-emerald-100 text-emerald-700',
  expected: 'bg-amber-100 text-amber-700',
  gone: 'bg-slate-200 text-slate-500',
  rolled: 'bg-slate-200 text-slate-500',
};

/** Box border/background per status, matching the avatar's tint family. */
const BOX_TONE: Record<ChildVM['status'], string> = {
  here: 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100',
  expected: 'bg-amber-50 border-amber-200 hover:bg-amber-100',
  gone: 'bg-slate-100 border-slate-200 opacity-70',
  rolled: 'bg-slate-100 border-slate-200 opacity-70',
};

function statusLabel(child: ChildVM): { text: string; tone: string } {
  switch (child.status) {
    case 'here':
      return {
        text: child.lastEventAt ? `In ${formatTime(child.lastEventAt)}` : 'Here',
        tone: 'text-emerald-700',
      };
    case 'gone':
      return {
        text: child.lastEventAt ? `Out ${formatTime(child.lastEventAt)}` : 'Gone',
        tone: 'text-slate-500',
      };
    case 'rolled':
      return { text: 'Rolled to next', tone: 'text-slate-500' };
    case 'expected':
    default:
      if (child.minutesLate > 0) {
        return { text: `${child.minutesLate}m late`, tone: 'text-rose-700' };
      }
      return { text: 'Expected', tone: 'text-amber-700' };
  }
}

/**
 * One clean, calm box for a single child on the live-session grid: a
 * soft-tinted avatar (status colour, not a loud fill), name, and a short
 * status label + time. Clicking toggles the caller's `expanded` state — the
 * expanded tile's contents are Task 6's concern, not this component's.
 */
const ChildBox: React.FC<{
  child: ChildVM;
  expanded: boolean;
  onToggle: () => void;
}> = ({ child, expanded, onToggle }) => {
  const label = statusLabel(child);
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={`child-box-${child.studentId}`}
      data-status={child.status}
      aria-expanded={expanded}
      className={`${BOX_TONE[child.status]} ${
        expanded ? 'ring-2 ring-primary-400' : ''
      } border rounded-md px-2 py-1.5 text-left flex items-center gap-2 transition w-full`}
    >
      <div
        aria-hidden="true"
        className={`${
          AVATAR_TINT[child.status]
        } w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0`}
      >
        {initialsOf(child.studentName)}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-slate-900 truncate">{child.studentName}</p>
        <p className={`text-[10px] ${label.tone}`}>{label.text}</p>
        {child.stayingOn && (
          <p
            data-testid={`staying-on-${child.studentId}`}
            className="text-[10px] text-violet-700 truncate"
          >
            Staying on → {child.stayingOn.nextClassName}
          </p>
        )}
      </div>
    </button>
  );
};

export default ChildBox;
